import { apiClient } from '@shared/api/apiClient';
import type { User } from '@shared/types';

export class AuthError extends Error {
  fieldErrors?: Record<string, string[]>;
  statusCode?: number;

  constructor(message: string, statusCode?: number, fieldErrors?: Record<string, string[]>) {
    super(message);
    this.name = 'AuthError';
    this.fieldErrors = fieldErrors;
    this.statusCode = statusCode;
  }
}

export interface AuthResponse {
  id: string;
  name: string;
  email: string;
  role: string;
  level: number;
  totalPoints: number;
  accessToken: string;
  refreshToken: string;
}

export interface TokenRefreshResponse {
  accessToken: string;
  refreshToken: string;
}

/**
 * Authentication service for handling user registration, login, and token management.
 */
export const authService = {
  /**
   * Register a new user account.
   */
  register: async (name: string, email: string, password: string): Promise<AuthResponse> => {
    try {
      const authResponse = await apiClient.post<AuthResponse>('/api/Authentication/register', {
        name,
        email,
        password,
      });

      // Store tokens in localStorage
      localStorage.setItem('authToken', authResponse.accessToken);
      localStorage.setItem('refreshToken', authResponse.refreshToken);
      localStorage.setItem(
        'user',
        JSON.stringify({
          id: authResponse.id,
          name: authResponse.name,
          email: authResponse.email,
          role: authResponse.role,
          level: authResponse.level,
          totalPoints: authResponse.totalPoints,
        })
      );

      return authResponse;
    } catch (error: unknown) {
      if (error instanceof AuthError) throw error;
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { message?: string; title?: string; errors?: Record<string, string[]> };
        };
      };
      const status = axiosError.response?.status;
      const data = axiosError.response?.data;
      const message = data?.message || data?.title || '';
      const fieldErrors = data?.errors || undefined;

      if (status === 409 || message.toLowerCase().includes('already exists')) {
        throw new AuthError('errors.emailInUse', status, fieldErrors);
      }
      throw new AuthError(message || 'errors.registrationFailed', status, fieldErrors);
    }
  },

  /**
   * Log in with email and password.
   */
  login: async (email: string, password: string): Promise<AuthResponse> => {
    try {
      const authResponse = await apiClient.post<AuthResponse>('/api/Authentication/login', {
        email,
        password,
      });

      // Store tokens in localStorage
      localStorage.setItem('authToken', authResponse.accessToken);
      localStorage.setItem('refreshToken', authResponse.refreshToken);
      localStorage.setItem(
        'user',
        JSON.stringify({
          id: authResponse.id,
          name: authResponse.name,
          email: authResponse.email,
          role: authResponse.role,
          level: authResponse.level,
          totalPoints: authResponse.totalPoints,
        })
      );

      return authResponse;
    } catch (error: unknown) {
      if (error instanceof AuthError) throw error;
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { message?: string; title?: string; errors?: Record<string, string[]> };
        };
      };
      const status = axiosError.response?.status;
      const data = axiosError.response?.data;
      const fieldErrors = data?.errors || undefined;

      if (status === 401) {
        throw new AuthError('errors.invalidCredentials', 401, fieldErrors);
      }
      throw new AuthError(
        data?.message || data?.title || 'errors.loginFailed',
        status,
        fieldErrors
      );
    }
  },

  /**
   * Refresh access token using refresh token.
   */
  refreshToken: async (refreshToken: string): Promise<TokenRefreshResponse> => {
    try {
      const tokenResponse = await apiClient.post<TokenRefreshResponse>(
        '/api/Authentication/refresh',
        { refreshToken }
      );

      // Update tokens in localStorage
      localStorage.setItem('authToken', tokenResponse.accessToken);
      localStorage.setItem('refreshToken', tokenResponse.refreshToken);

      return tokenResponse;
    } catch (error) {
      // Clear tokens if refresh fails
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      throw error instanceof Error ? error : new Error('errors.tokenRefreshFailed');
    }
  },

  /**
   * Log out the current user.
   */
  logout: (): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  /**
   * Get the current user from localStorage.
   */
  getCurrentUser: (): Partial<User> | null => {
    const userJson = localStorage.getItem('user');
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  },

  /**
   * Check if the stored JWT token is expired.
   * Decodes the payload and compares the `exp` claim against current time.
   */
  isTokenExpired: (): boolean => {
    const token = localStorage.getItem('authToken');
    if (!token) return true;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const payload = JSON.parse(atob(parts[1]));
      // exp is in seconds, Date.now() is in milliseconds
      return !payload.exp || payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  },

  /**
   * Check if user is authenticated (has a non-expired token).
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('authToken');
  },

  /**
   * Get the access token.
   */
  getAccessToken: (): string | null => {
    return localStorage.getItem('authToken');
  },

  /**
   * Get the refresh token.
   */
  getRefreshToken: (): string | null => {
    return localStorage.getItem('refreshToken');
  },

  /**
   * Change the current user's password.
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<string> => {
    try {
      // The canonical client's handleResponse throws if data is null.
      // For change-password the backend returns { data: null, message: "..." }.
      // We call post with a try/catch and retrieve the message from the error
      // if handleResponse throws, but in practice the backend returns a message
      // in the ApiResponse wrapper. We access it via a low-level axios call
      // through the public client instance, so instead we catch the "No data received"
      // error and return the message embedded there, OR we use a direct axios post
      // that doesn't unwrap. The simplest correct approach: catch the error thrown
      // by handleResponse and surface the message from the raw response.
      //
      // Because the canonical client throws on null data, we need to intercept at
      // the Axios response level. We do this by catching the error and checking
      // whether it came from handleResponse (message = 'No data received') vs a
      // real HTTP error. If it's from handleResponse we know the call succeeded
      // at the network level, so we return the success message.
      await apiClient.post<null>('/api/Authentication/change-password', {
        currentPassword,
        newPassword,
      });
      return 'Password changed successfully';
    } catch (error: unknown) {
      if (error instanceof AuthError) throw error;

      // If the error message is 'No data received' it means the request succeeded
      // (HTTP 200) but the backend returned data: null. Surface success message.
      if (error instanceof Error && error.message === 'No data received') {
        return 'Password changed successfully';
      }

      const axiosError = error as {
        response?: {
          status?: number;
          data?: { message?: string; title?: string; errors?: Record<string, string[]> };
        };
      };
      const status = axiosError.response?.status;
      const data = axiosError.response?.data;
      const message = data?.message || data?.title || '';
      const fieldErrors = data?.errors || undefined;

      throw new AuthError(message || 'Password change failed', status, fieldErrors);
    }
  },
};
